import React from 'react';

export interface GlassTableColumn {
  key: string;
  label: string;
  width?: string;
  render?: (value: any, row: any) => React.ReactNode;
}

export interface GlassTableProps {
  columns: GlassTableColumn[];
  data: Array<Record<string, any>>;
  rowKey: string;
  onRowClick?: (row: any) => void;
  className?: string;
  emptyMessage?: string;
}

export const GlassTable: React.FC<GlassTableProps> = ({
  columns,
  data,
  rowKey,
  onRowClick,
  className = '',
  emptyMessage = 'No data available',
}) => {
  const containerStyles: React.CSSProperties = {
    width: '100%',
    overflowX: 'auto',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--stroke)',
    background: 'var(--panel)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  };

  const tableStyles: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'var(--text-sm)',
  };

  const headerStyles: React.CSSProperties = {
    background: 'var(--panel-strong)',
    borderBottom: '1px solid var(--stroke)',
  };

  const headerCellStyles: React.CSSProperties = {
    padding: 'var(--space-3) var(--space-4)',
    textAlign: 'left',
    fontWeight: 'var(--font-semibold)',
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-xs)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const rowStyles = (isClickable: boolean): React.CSSProperties => ({
    borderBottom: '1px solid var(--divider)',
    cursor: isClickable ? 'pointer' : 'default',
    transition: 'background var(--transition-fast)',
  });

  const cellStyles: React.CSSProperties = {
    padding: 'var(--space-3) var(--space-4)',
    color: 'var(--text)',
    verticalAlign: 'middle',
  };

  const emptyStyles: React.CSSProperties = {
    padding: 'var(--space-8)',
    textAlign: 'center',
    color: 'var(--text-muted)',
  };

  return (
    <div className={`glass-table-container ${className}`} style={containerStyles}>
      <table style={tableStyles}>
        <thead style={headerStyles}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                style={{
                  ...headerCellStyles,
                  width: column.width,
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={emptyStyles}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={row[rowKey]}
                style={rowStyles(!!onRowClick)}
                onClick={() => onRowClick?.(row)}
                onMouseEnter={(e) => {
                  if (onRowClick) {
                    e.currentTarget.style.background = 'var(--panel-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {columns.map((column) => (
                  <td key={`${row[rowKey]}-${column.key}`} style={cellStyles}>
                    {column.render
                      ? column.render(row[column.key], row)
                      : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GlassTable;
